import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { LcncAppPicker } from './LcncAppPicker.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface WorkflowRow {
  readonly workflowId: string;
  readonly workflowCode: string;
  readonly workflowName: string;
  readonly workflowType: string;
  readonly status: string;
  readonly versionNo: number;
}

const WORKFLOW_TYPES = ['', 'APPROVAL', 'ASSIGNMENT', 'ESCALATION', 'REVIEW', 'VERIFICATION', 'NOTIFICATION', 'EXCEPTION'];
const STATUSES = ['', 'DRAFT', 'TESTING', 'PUBLISHED', 'ARCHIVED'];
const CREATE_TYPES = WORKFLOW_TYPES.filter(Boolean);

/**
 * M28 — Workflow Studio (§11 / §32). Visual workflow designer integrated with
 * the M04 workflow engine across the seven workflow archetypes. Lists every
 * workflow_definition (T369) with its type, lifecycle status and version,
 * filterable by workflow type and status. Supports the create → test → deploy →
 * rollback (BR-07) journey, each wired to the guarded /v1/lcnc/workflows write API.
 */
export function M28WorkflowStudioPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<WorkflowRow[]>([]);
  const [workflowType, setWorkflowType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [applicationId, setApplicationId] = useState('');
  const [name, setName] = useState('');
  const [newType, setNewType] = useState('APPROVAL');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (workflowType) params.set('workflowType', workflowType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: WorkflowRow[] }>(`/v1/lcnc/workflows${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, workflowType, status, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m28.workflowStudio.notAuthorised', { verb, perm })
      : (err as Error).message;

  const runRowAction = async (fn: () => Promise<unknown>, successMsg: string, verb: string, perm: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, verb, perm));
    }
  };

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!applicationId.trim()) { setActionError(tr('m28.workflowStudio.validationApplicationId')); return; }
    if (!name.trim()) { setActionError(tr('m28.workflowStudio.validationName')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: WorkflowRow }>(`/v1/lcnc/workflows`, {
        method: 'POST',
        body: { applicationId: applicationId.trim(), workflowName: name.trim(), workflowType: newType },
        token,
      });
      setNotice(tr('m28.workflowStudio.noticeCreated', { code: r.data.workflowCode }));
      setName('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, tr('m28.workflowStudio.verbCreate'), 'WORKFLOW_DESIGN'));
    } finally {
      setBusy(false);
    }
  };

  const test = (w: WorkflowRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/lcnc/workflows/${w.workflowId}/test`, { method: 'POST', body: {}, token }),
      tr('m28.workflowStudio.noticeTested', { code: w.workflowCode }), tr('m28.workflowStudio.verbTest'), 'WORKFLOW_DESIGN',
    );

  const deploy = (w: WorkflowRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/lcnc/workflows/${w.workflowId}/deploy`, { method: 'POST', body: {}, token }),
      tr('m28.workflowStudio.noticeDeployed', { code: w.workflowCode }), tr('m28.workflowStudio.verbDeploy'), 'WORKFLOW_DESIGN',
    );

  const rollback = (w: WorkflowRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/lcnc/workflows/${w.workflowId}/rollback`, { method: 'POST', body: {}, token }),
      tr('m28.workflowStudio.noticeRolledBack', { code: w.workflowCode }), tr('m28.workflowStudio.verbRollback'), 'ROLLBACK',
    );

  return (
    <main aria-label="workflow-studio">
      <h1>{tr('m28.workflowStudio.title')}</h1>

      <section aria-label="create-workflow">
        <h2>{tr('m28.workflowStudio.newHeading')}</h2>
        <form aria-label="workflow-form" onSubmit={(e) => void create(e)}>
          <LcncAppPicker label={tr('m28.workflowStudio.fieldApplicationId')} value={applicationId} onPick={setApplicationId} />
          <label>{tr('m28.workflowStudio.fieldName')}<input aria-label="field-workflow-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>{tr('m28.workflowStudio.fieldType')}
            <select aria-label="field-workflow-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-workflow" disabled={busy}>{busy ? tr('m28.workflowStudio.creating') : tr('m28.workflowStudio.create')}</button>
        </form>
        {actionError && <p role="alert" aria-label="workflow-error">{actionError}</p>}
        {notice && <p aria-label="workflow-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m28.workflowStudio.filterType')}
          <select aria-label="filter-workflow-type" value={workflowType} onChange={(e) => setWorkflowType(e.target.value)}>
            {WORKFLOW_TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m28.workflowStudio.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m28.workflowStudio.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m28.workflowStudio.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m28.workflowStudio.loading')}</p> : (
        <>
          <p aria-label="workflow-count">{tr('m28.workflowStudio.count', { count: rows.length })}</p>
          <table aria-label="workflow-table">
            <thead><tr><th>{tr('m28.workflowStudio.colCode')}</th><th>{tr('m28.workflowStudio.colName')}</th><th>{tr('m28.workflowStudio.colType')}</th><th>{tr('m28.workflowStudio.colStatus')}</th><th>{tr('m28.workflowStudio.colVersion')}</th><th>{tr('m28.workflowStudio.colActions')}</th></tr></thead>
            <tbody>
              {rows.map((w) => (
                <tr key={w.workflowId}>
                  <td>{w.workflowCode}</td><td>{w.workflowName}</td><td>{w.workflowType}</td>
                  <td>{w.status}</td><td>v{w.versionNo}</td>
                  <td>
                    <button type="button" aria-label={`test-${w.workflowCode}`} onClick={() => void test(w)}>{tr('m28.workflowStudio.btnTest')}</button>
                    <button type="button" aria-label={`deploy-${w.workflowCode}`} onClick={() => void deploy(w)}>{tr('m28.workflowStudio.btnDeploy')}</button>
                    <button type="button" aria-label={`rollback-${w.workflowCode}`} onClick={() => void rollback(w)}>{tr('m28.workflowStudio.btnRollback')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
